import { onLine } from './lines';
import type { PartLine, TempoPoint, Timeline, TimelineNote, VoicePart } from './model';

/** Where playback is, in the timeline and in the written score (for the cursor). */
export interface ScorePosition {
  timelineMeasureIndex: number;
  /** Index of the written measure. */
  measureIndex: number;
  measureNumber: string;
  /** Quarter notes from the start of the written measure. */
  beatOffset: number;
  /** Quarter notes from the start of the timeline. */
  beat: number;
  sec: number;
}

/** Inverse of secondsAtBeat: seconds to beats (quarter notes) along a tempo map. */
export function beatAtSeconds(tempoMap: TempoPoint[], sec: number, tempoFactor = 1): number {
  let point = tempoMap[0];
  if (!point) return 0;
  for (const p of tempoMap) {
    if (p.sec <= sec) point = p;
    else break;
  }
  return point.beat + ((sec - point.sec) * point.quarterBpm * tempoFactor) / 60;
}

/** The position at a time in seconds, or null outside the timeline. */
export function positionAtSeconds(timeline: Timeline, sec: number): ScorePosition | null {
  const { measures } = timeline;
  if (measures.length === 0 || sec < 0 || sec >= timeline.totalSec) return null;
  let lo = 0;
  let hi = measures.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((measures[mid]?.startSec ?? Infinity) <= sec) lo = mid;
    else hi = mid - 1;
  }
  const m = measures[lo];
  if (!m) return null;
  const beat = beatAtSeconds(timeline.tempoMap, sec, timeline.tempoFactor);
  const beatOffset = Math.min(Math.max(0, beat - m.startBeat), m.durationBeats);
  return {
    timelineMeasureIndex: m.timelineIndex,
    measureIndex: m.measureIndex,
    measureNumber: m.measureNumber,
    beatOffset: Math.round(beatOffset * 1e6) / 1e6,
    beat,
    sec,
  };
}

/**
 * The timeline measures that play written measures `from` to `to` (indexes), taking the first
 * time `from` is played and then the first time `to` is played after it. With repeats, bars
 * 2–3 of "1 2 3 4 2 3 5" are timeline measures 1–2. The whole song (its first written bar to its
 * last) is every bar as played, repeats and jumps included, also when the last bar is inside a
 * repeat or the song ends at a Fine before it. Returns null if the range is empty.
 */
export function timelineRange(
  timeline: Timeline,
  fromMeasureIndex: number,
  toMeasureIndex: number,
): { first: number; last: number } | null {
  const measures = timeline.measures;
  if (measures.length > 0) {
    const lowest = Math.min(...measures.map((m) => m.measureIndex));
    const highest = Math.max(...measures.map((m) => m.measureIndex));
    if (fromMeasureIndex === lowest && toMeasureIndex === highest) return { first: 0, last: measures.length - 1 };
  }
  const first = timeline.measures.findIndex((m) => m.measureIndex === fromMeasureIndex);
  if (first < 0) return null;
  const last = timeline.measures.findIndex((m, i) => i >= first && m.measureIndex === toMeasureIndex);
  return last < 0 ? null : { first, last };
}

/** The first note of a voice part starting at or after a time, for "play my first note". */
export function firstNoteFrom(timeline: Timeline, voicePart: VoicePart, sec: number, line: PartLine = 1): TimelineNote | null {
  return timeline.notes.find((n) => n.voicePart === voicePart && onLine(n, line) && n.startSec >= sec - 1e-9) ?? null;
}
